import { styled } from "styled-components";
import { colors } from "../../../theme/colors";
import { Link, useParams } from "react-router-dom";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { Row } from "../../../components/ui/grid/row.component";
import { Spacer } from "../../../components/ui/spacer/spacer.component";
import { Pill } from "../../../components/ui/pill/pill.component";
import { Card, Tabs } from "antd";
import _ from "lodash";
import { CastView } from "../../series/components/cast-view.component";
import { ViewLayout } from "../sections/view.layout";
import { useEffect, useState } from "react";
import { FilmProfile } from "../../../models/film-profile.model";
import { axios } from "../../../api/axios";
import { handleResponse } from "../../../utils/functions/http/handleResponse";
import dayjs from "dayjs";
import { valueToLabel } from "../../../utils/functions/misc/valueToLabel";
import { useAtomValue } from "jotai";
import { masterAtom } from "../../../atoms/masters.atom";
import { countryCodeToName } from "../../../utils/functions/misc/countryCodeToName";
import { Distribution } from "../../../models/distribution.model";
import ReactHlsPlayer from "react-hls-video-player/dist/components/ReactHlsPlayer";
import { Toaster } from "react-hot-toast";
import { Document, Page, pdfjs } from "react-pdf";
import { authAtom } from "../../../atoms/auth.atom";
import { Helmet } from "react-helmet-async";
import { AxiosError } from "axios";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url
).toString();

export const ViewFilm = () => {
  const { customURL } = useParams();

  const master = useAtomValue(masterAtom);
  const auth = useAtomValue(authAtom);

  const [id, setId] = useState("");
  const [film, setFilm] = useState<FilmProfile | null>(null);
  const [distribution, setDistribution] = useState<Distribution | null>(null);
  const [hasFetchedDistribution, setHasFetchedDistribution] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (film) return;
    const filmKey = customURL || id;
    if (!filmKey) return;
    let cancelled = false;

    const loadOwnerFilm = () =>
      axios.get<FilmProfile>("/client/film", {
        headers: { "X-FILM-ID": filmKey },
      });

    // The public profile only includes published films. A draft the owner
    // just saved still has to load through the authenticated film endpoint.
    const request = customURL
      ? axios
          .get<FilmProfile>("/client/profile/film", {
            headers: { "X-FILM-ID": customURL },
          })
          .catch((error: unknown) => {
            if (error instanceof AxiosError && error.response?.status === 404) {
              return loadOwnerFilm();
            }
            throw error;
          })
      : loadOwnerFilm();

    request
      .then((resp) => {
        if (cancelled) return;
        setLoadFailed(false);
        setFilm(resp.data);
        if (!id) setId(resp.data.id);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadFailed(true);
        handleResponse(error);
      });

    return () => {
      cancelled = true;
    };
  }, [customURL, film, id]);

  useEffect(() => {
    if (id && film?.is_distributed && !hasFetchedDistribution) {
      axios
        .get<Distribution>("/client/distribution", {
          headers: {
            "X-Project-Id": id,
            "X-Project-Type": "film",
          },
        })
        .then((response) => {
          setHasFetchedDistribution(true);
          setDistribution(response.data);
        })
        .catch((e) => {
          if (e instanceof AxiosError && e.response?.status === 404) return;

          return handleResponse(e);
        });
    }
  }, [id, film, hasFetchedDistribution]);

  console.log(film);

  return (
    <ViewLayout
      id={id ?? ""}
      customUrl={customURL ?? ""}
      title={film?.title ?? (loadFailed ? "Film not found" : undefined)}
      loading={!film && !loadFailed}
      viewOnly={film?.user !== auth.user?.id}
      details={
        film
          ? [
              valueToLabel("languages", film.language, master)?.toString() ??
                "",
              `${
                (film.pf_censor
                  ? valueToLabel(
                      "censor",
                      film.pf_censor,
                      master
                    )?.toString() ?? ""
                  : "") === "All Ages"
                  ? ""
                  : "Ages "
              }${
                film.pf_censor
                  ? valueToLabel(
                      "censor",
                      film.pf_censor,
                      master
                    )?.toString() ?? ""
                  : ""
              }`,
              dayjs(film.date_of_completion, "DD-MM-YYYY").format("YYYY"),
              typeof film.duration === "string"
                ? film.duration
                : `${film.duration.mm}m ${film.duration.ss}s`,
            ]
          : []
      }
      distributed={film?.is_distributed}
      status={film?.status}
    >
      <Helmet>
        {film ? (
          <>
            <meta name="description" content={film.synopsis} />
            <meta name="title" content={`Pocketfilms | ${film.title}`} />
            <meta
              property="og:image"
              content={
                film.film_image?.find((image) => image.aspect_ratio === "16_9")
                  ?.image
              }
            />
            <meta
              property="og:image:secure_url"
              content={
                film.film_image?.find((image) => image.aspect_ratio === "16_9")
                  ?.image
              }
            />
            <meta property="og:image:type" content="image/jpeg" />
            <meta property="og:image:width" content="200" />
            <meta
              property="og:image:alt"
              content={`Poster for ${film.title}`}
            />
          </>
        ) : null}
      </Helmet>
      {!!film ? (
        <>
          {customURL ? (
            <Toaster
              toastOptions={{
                style: {
                  fontSize: "1.3rem",
                },
              }}
            />
          ) : null}
          <TechnicalDetailsText>
            Shot in&nbsp;&nbsp;
            <span>{countryCodeToName(film.country?.toUpperCase())}</span> on
            &nbsp;&nbsp;
            <span>
              {dayjs(film.date_of_completion, "DD-MM-YYYY").format(
                "DD-MM-YYYY"
              )}
            </span>
          </TechnicalDetailsText>
          <Spacer direction="bottom" size={20} />
          <Row style={{ width: "100%" }}>
            <VerticalPoster
              $src={
                film.film_image?.find((image) => image.aspect_ratio === "3_4")
                  ?.image ?? ""
              }
            />
            <HorizontalPoster
              $src={
                film.film_image?.find((image) => image.aspect_ratio === "16_9")
                  ?.image ?? ""
              }
            />
          </Row>
          <Spacer size={10} direction="bottom" />
          <Row $gap={5}>
            {(film.genre ?? []).map((genre, i) => (
              <Pill
                key={`Genre__${i}`}
                style={{
                  backgroundColor: colors.primary.hex,
                  color: colors.white.hex,
                }}
              >
                {valueToLabel("genre", genre, master)}
              </Pill>
            ))}
            {/* {film.keywords?.map((keyword, i) => (
              <Pill
                key={`Keyword__${i}`}
                style={{
                  backgroundColor: colors.grey.hex,
                  color: colors.black.hex,
                }}
              >
                "{keyword}"
              </Pill>
            ))} */}
          </Row>
          <Spacer size={20} direction="bottom" />
          <Row style={{ alignItems: "flex-start" }}>
            <Tabs
              defaultActiveKey="1"
              // tabBarStyle={{
              //   color: "white",
              //   backgroundColor: colors.white.hex,
              //   padding: "0 2rem",
              //   borderRadius: "0.75rem 0.75rem 0 0",
              // }}
              style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
              items={[
                {
                  key: "1",
                  label: "Synopsis",
                  children: (
                    <Synopsis>
                      {film.synopsis}
                      <br />
                      <br />
                      Keywords: {film.keywords?.join(", ") ?? ""}
                    </Synopsis>
                  ),
                },
                {
                  key: "2",
                  label: "Cast and Crew",
                  children: (
                    <>
                      <SectionTitle to="">
                        Directors&nbsp;
                        {/* <MdChevronRight /> */}
                      </SectionTitle>
                      <Spacer size={10} direction="bottom" />
                      <Row
                        style={{
                          gap: "2rem",
                          overflowX: "scroll",
                          maxWidth: "95vw",
                        }}
                      >
                        {(film.film_crew ?? [])
                          .filter((crew) => crew.role === "director")
                          .map((director) => (
                            <CastView
                              image={director.profile_url}
                              key={director.id}
                              name={director.name}
                              description=""
                            />
                          ))}
                      </Row>
                      <Spacer size={30} direction="bottom" />
                      <SectionTitle to="">
                        Producers&nbsp;
                        {/* <MdChevronRight /> */}
                      </SectionTitle>
                      <Spacer size={10} direction="bottom" />
                      <Row
                        style={{
                          gap: "2rem",
                          overflowX: "scroll",
                          maxWidth: "95vw",
                        }}
                      >
                        {(film.film_crew ?? [])
                          .filter((crew) => crew.role === "producer")
                          .map((producer) => (
                            <CastView
                              image={producer.profile_url}
                              key={producer.id}
                              name={producer.name}
                              description=""
                            />
                          ))}
                      </Row>
                      <Spacer size={30} direction="bottom" />
                      <SectionTitle to="">
                        Cast&nbsp;
                        {/* <MdChevronRight /> */}
                      </SectionTitle>
                      <Spacer size={10} direction="bottom" />
                      <Row
                        style={{
                          gap: "2rem",
                          overflowX: "scroll",
                          maxWidth: "95vw",
                        }}
                      >
                        {(film.film_cast ?? []).map((cast) => (
                          <CastView
                            image={cast.profile_url}
                            key={cast.id}
                            name={cast.name}
                            description={cast.character}
                          />
                        ))}
                      </Row>
                      <Spacer size={30} direction="bottom" />
                      <SectionTitle to="">
                        Crew&nbsp;
                        {/* <MdChevronRight /> */}
                      </SectionTitle>
                      <Spacer size={10} direction="bottom" />
                      <Row
                        style={{
                          gap: "2rem",
                          overflowX: "scroll",
                          maxWidth: "95vw",
                        }}
                      >
                        {(film.film_crew ?? [])
                          .filter(
                            (crew) =>
                              crew.role !== "producer" &&
                              crew.role !== "director"
                          )
                          .map((crew) => (
                            <CastView
                              key={crew.id}
                              image={crew.profile_url}
                              name={crew.name}
                              description={
                                valueToLabel(
                                  "crew_role",
                                  crew.role,
                                  master,
                                  true
                                )?.toString() ?? ""
                              }
                            />
                          ))}
                      </Row>
                    </>
                  ),
                },
                {
                  key: "3",
                  label: "Awards",
                  children: (
                    <>
                      <SectionTitle to="">Awards</SectionTitle>
                      <Spacer size={10} direction="bottom" />
                      <Row
                        style={{
                          gap: "1rem",
                          overflowX: "scroll",
                          maxWidth: "95vw",
                        }}
                      >
                        {(film.film_award ?? [])?.map((award) => (
                          <Card
                            key={award.id}
                            style={{
                              minWidth: "30rem",
                              maxWidth: "30rem",
                              position: "relative",
                            }}
                            title={`${valueToLabel(
                              "festival_status",
                              award.award_status,
                              master
                            )} - ${award.name_of_festival}`}
                            size="small"
                          >
                            {award.award_winner_name} •{" "}
                            {award.award_year as number} •{" "}
                            {countryCodeToName(award.country)}
                          </Card>
                        ))}
                      </Row>
                    </>
                  ),
                },
                {
                  key: "4",
                  label: "Censor Ratings",
                  children: (
                    <Synopsis>
                      <b style={{ width: "20rem", display: "inline-block" }}>
                        Censor:
                      </b>{" "}
                      {valueToLabel("censor", film.pf_censor, master)}
                      <br />
                      <b style={{ width: "20rem", display: "inline-block" }}>
                        Language:
                      </b>{" "}
                      {valueToLabel(
                        "language_ratings",
                        film.language_rating,
                        master
                      )}
                      <br />
                      <b style={{ width: "20rem", display: "inline-block" }}>
                        Violence:
                      </b>{" "}
                      {valueToLabel(
                        "violence_ratings",
                        film.violence_rating,
                        master
                      )}
                      <br />
                      <b style={{ width: "20rem", display: "inline-block" }}>
                        Adult Content:
                      </b>{" "}
                      {valueToLabel(
                        "adult_content_ratings",
                        film.adult_content_rating,
                        master
                      )}
                      <br />
                      <b style={{ width: "20rem", display: "inline-block" }}>
                        Drugs:
                      </b>{" "}
                      {valueToLabel("drugs_ratings", film.drug_rating, master)}
                      <br />
                      <b style={{ width: "20rem", display: "inline-block" }}>
                        Harmful / Dangerous Act:
                      </b>{" "}
                      {valueToLabel(
                        "harmful_ratings",
                        film.harmful_rating,
                        master
                      )}
                      <br />
                      <b style={{ width: "20rem", display: "inline-block" }}>
                        Hateful and Derogatory:
                      </b>{" "}
                      {valueToLabel(
                        "hateful_ratings",
                        film.hateful_rating,
                        master
                      )}
                    </Synopsis>
                  ),
                },
                {
                  key: "5",
                  label: "Other Information",
                  children: (
                    <>
                      <SectionTitle to="">Director's Statement</SectionTitle>
                      <Spacer size={10} direction="bottom" />
                      <Synopsis>{film.director_statement || "-"}</Synopsis>
                      <Spacer size={20} direction="bottom" />
                      <SectionTitle to="">Producer's Statement</SectionTitle>
                      <Spacer size={10} direction="bottom" />
                      <Synopsis>{film.producer_statement || "-"}</Synopsis>
                      <Spacer size={20} direction="bottom" />
                      <SectionTitle to="">Other Information</SectionTitle>
                      <Spacer size={10} direction="bottom" />
                      <Synopsis>{film.other_information || "-"}</Synopsis>
                    </>
                  ),
                },
                ...(!customURL
                  ? [
                      {
                        key: "6",
                        label: "Videos",
                        children: (
                          <Row
                            style={{
                              gap: "1rem",
                              overflowX: "scroll",
                              maxWidth: "90%",
                            }}
                          >
                            {distribution?.videos?.find(
                              (video) => video.video_type === "primary-trailer"
                            )?.video_id ? (
                              <Card
                                style={{
                                  minWidth: distribution?.videos?.find(
                                    (video) =>
                                      video.video_type === "primary-trailer"
                                  )?.video_id
                                    ? "45rem"
                                    : "15rem",
                                  maxWidth: "45rem",
                                  position: "relative",
                                }}
                                title="Horizontal Trailer"
                                size="small"
                              >
                                <iframe
                                  src={`https://iframe.mediadelivery.net/embed/102339/${
                                    distribution.videos.find(
                                      (video) =>
                                        video.video_type === "primary-trailer"
                                    )!.video_id
                                  }?autoplay=false`}
                                  loading="lazy"
                                  style={{
                                    border: "none",
                                    width: "100%",
                                    height: "25rem",
                                  }}
                                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                                  allowFullScreen
                                ></iframe>
                              </Card>
                            ) : null}
                            {distribution?.videos?.find(
                              (video) =>
                                video.video_type === "additional-trailer"
                            )?.video_id ? (
                              <Card
                                style={{
                                  minWidth: distribution?.videos?.find(
                                    (video) =>
                                      video.video_type === "additional-trailer"
                                  )?.video_id
                                    ? "45rem"
                                    : "15rem",
                                  maxWidth: "45rem",
                                  position: "relative",
                                }}
                                title="Vertical Trailer"
                                size="small"
                              >
                                <iframe
                                  src={`https://iframe.mediadelivery.net/embed/102339/${
                                    distribution.videos.find(
                                      (video) =>
                                        video.video_type ===
                                        "additional-trailer"
                                    )!.video_id
                                  }?autoplay=false`}
                                  loading="lazy"
                                  style={{ border: "none", width: "100%" }}
                                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                                  allowFullScreen
                                ></iframe>
                              </Card>
                            ) : null}
                            {distribution?.videos?.find(
                              (video) => video.video_type === "primary-video"
                            )?.video_id ? (
                              <Card
                                style={{
                                  minWidth: distribution?.videos?.find(
                                    (video) =>
                                      video.video_type === "primary-video"
                                  )?.video_id
                                    ? "45rem"
                                    : "15rem",
                                  maxWidth: "45rem",
                                  position: "relative",
                                }}
                                title="Film Video"
                                size="small"
                              >
                                <iframe
                                  src={`https://iframe.mediadelivery.net/embed/102339/${
                                    distribution.videos.find(
                                      (video) =>
                                        video.video_type === "primary-video"
                                    )!.video_id
                                  }?autoplay=false`}
                                  loading="lazy"
                                  style={{
                                    border: "none",
                                    width: "100%",
                                    height: "25rem",
                                  }}
                                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                                  allowFullScreen
                                ></iframe>
                              </Card>
                            ) : null}
                            {distribution?.videos?.find(
                              (video) => video.video_type === "additional-video"
                            )?.video_id ? (
                              <Card
                                style={{
                                  minWidth: distribution?.videos?.find(
                                    (video) =>
                                      video.video_type === "additional-video"
                                  )?.video_id
                                    ? "45rem"
                                    : "15rem",
                                  maxWidth: "45rem",
                                  position: "relative",
                                }}
                                title="Vertical Video"
                                size="small"
                              >
                                <iframe
                                  src={`https://iframe.mediadelivery.net/embed/102339/${
                                    distribution.videos.find(
                                      (video) =>
                                        video.video_type === "additional-video"
                                    )!.video_id
                                  }?autoplay=false`}
                                  loading="lazy"
                                  style={{ border: "none", width: "100%" }}
                                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                                  allowFullScreen
                                ></iframe>
                              </Card>
                            ) : null}
                          </Row>
                        ),
                      },
                    ]
                  : []),
                ...(distribution?.agreement_url
                  ? [
                      {
                        key: "7",
                        label: "Agreement",
                        children: (
                          <>
                            <iframe
                              src={distribution.agreement_url}
                              style={{ width: "50vw", height: "70.7vw", minHeight: "800px" }}
                            ></iframe>
                          </>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
            <Spacer size={20} direction="right" />
            {/* <Column
              style={{
                minWidth: "25%",
                marginTop: "2rem",
                transform: "translateY(100%)",
              }}
            >
              <ShootingFormat>
                <MdVideocam color={colors.text.hex} size={120} />
                <span>
                  {valueToLabel(
                    "shooting_format",
                    film.shooting_format,
                    master
                  )}
                  <br />
                  {valueToLabel("aspect_ratio", film.aspect_ratio, master)}
                </span>
              </ShootingFormat>
              <Spacer size={40} direction="bottom" />
              
              <Spacer size={-40} direction="bottom" />
            </Column> */}
          </Row>
          <Spacer size={20} direction="bottom" />
        </>
      ) : (
        <></>
      )}
    </ViewLayout>
  );
};
//https://m.media-amazon.com/images/M/MV5BNDVkYjU0MzctMWRmZi00NTkxLTgwZWEtOWVhYjZlYjllYmU4XkEyXkFqcGdeQXVyNTA4NzY1MzY@._V1_QL75_UY281_CR1,0,190,281_.jpg

const Poster = styled.div<{
  $src: string;
}>`
  border-radius: 0.75rem;
  height: 35rem;
  background-image: url(${({ $src }) => $src});
  background-size: contain;
`;

const VerticalPoster = styled(Poster)`
  flex: 0.25;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`;

const HorizontalPoster = styled(Poster)`
  flex: 0.75;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`;

const Synopsis = styled.p`
  font-size: 1.5rem;
  color: ${colors.text.hex};
  font-weight: 400;
`;

const ShootingFormat = styled.div`
  position: relative;
  font-size: 1.4rem;
  color: ${colors.black.hex};
  font-weight: 600;

  & > * {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  & span {
    transform: translate(-50%, -50%) translateX(-1rem);
  }
`;

const SectionTitle = styled(Link)`
  display: inline-flex;
  align-items: center;
  font-size: 2.5rem;
  font-weight: 600;
  border-left: 3px solid ${colors.primary.hex};
  padding-left: 1.2rem;
  color: ${colors.text.hex};
  text-decoration: none;

  & svg {
    transition: all 0.4s;
    transform: translateY(0.15rem);
  }

  &:hover svg {
    transform: translateY(0.15rem) translateX(0.5rem);
  }
`;

const TechnicalDetailsText = styled.h3`
  font-size: 1.5rem;
  font-weight: 500;
  text-align: center;

  & span {
    font-weight: 600;
    color: ${colors.primary.hex};
  }
`;
